'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@/comps/primitives/button';
import { AiProficiencyScoreCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-score-card';
import { MethodologyDrawer } from '@/domains/pages/ai-proficiency/components/methodology-drawer';
import { AiProficiencyReportCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-report-card';
import { LevelMixChart } from '@/domains/pages/ai-proficiency/components/level-mix-chart';
import { sumLevelUserCounts } from '@/domains/pages/ai-proficiency/utils/ladder';
import { ProficiencyLadder } from '@/domains/pages/ai-proficiency/components/proficiency-ladder';
import { ProficiencyOverTimeChart, ProficiencyOverTimeLegend } from '@/domains/pages/ai-proficiency/components/proficiency-over-time-chart';
import { useAiProficiencyPageData } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-page-data';
import { useAiProficiencyShellLoading } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-shell-loading';
import { AiProficiencyChartsLoadingSkeleton } from '@/domains/pages/ai-proficiency/ai-proficiency-page-loading';
import { shouldHideNebulyPageBody, useSyncNebulyPageViewState } from '@/domains/pages/hooks/use-sync-nebuly-page-view-state';
import type { NebulyPageBodyProps } from '@/domains/pages/types';
import { TimeRangeKind } from '@/api/generated/model';
import { useProjectsContext } from '@/providers/projects-provider';
import { Info } from 'lucide-react';
import { useState } from 'react';
export function AiProficiencyPageBody(t0) {
  const $ = _c(30);
  const {
    pageKey
  } = t0;
  const {
    projectId
  } = useProjectsContext();
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const data = useAiProficiencyPageData(t1);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const isEmpty = !data.isInitialLoading && !data.isChartsLoading && !data.isError && data.orgScore.score == null && data.orgScore.totalUsers === 0;
  const showPageShellLoading = useAiProficiencyShellLoading(pageKey, data.isInitialLoading, data.isError);
  let t2;
  if ($[2] !== data.error || $[3] !== data.isError || $[4] !== data.refetch || $[5] !== isEmpty || $[6] !== showPageShellLoading) {
    t2 = {
      isLoading: showPageShellLoading,
      isError: data.isError,
      error: data.error,
      refetch: data.refetch,
      isEmpty
    };
    $[2] = data.error;
    $[3] = data.isError;
    $[4] = data.refetch;
    $[5] = isEmpty;
    $[6] = showPageShellLoading;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useSyncNebulyPageViewState(t2);
  if (showPageShellLoading || data.isError) {
    return null;
  }
  if (shouldHideNebulyPageBody(false, false, isEmpty)) {
    return null;
  }
  const hasBenchmarks = data.orgBenchmarks != null;
  const t3 = `nebuly-page-body-${pageKey}`;
  let t4;
  if ($[8] !== data.isChartsLoading || $[9] !== data.isInitialLoading || $[10] !== data.levelBenchmarks || $[11] !== data.levelCountsOverTime || $[12] !== data.levelMixOverTime || $[13] !== data.levels || $[14] !== data.migration || $[15] !== data.migrationTimeRangeLabel || $[16] !== data.orgBenchmarks || $[17] !== data.orgScore || $[18] !== data.orgScoresOverTime || $[19] !== data.trendBenchmarks || $[20] !== data.trendLabels || $[21] !== hasBenchmarks) {
    t4 = data.isInitialLoading || data.isChartsLoading ? <AiProficiencyChartsLoadingSkeleton /> : <><div className="grid grid-cols-3 items-stretch border-b border-b-lightborder"><div className="col-span-1 flex border-r border-r-lightborder"><AiProficiencyReportCard title="AI Proficiency score" className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col justify-center !py-3" headerTrailing={<Button variant="outline" size="sm" className="h-7 gap-1.5 rounded-full px-3 text-xs text-muted-foreground hover:text-foreground" onClick={() => setMethodologyOpen(true)}><Info className="size-3.5" />How it's calculated</Button>}><AiProficiencyScoreCard orgScore={data.orgScore} benchmarks={data.orgBenchmarks} timeRangeKind={TimeRangeKind["30_days"]} /></AiProficiencyReportCard></div><div className="col-span-2 flex"><AiProficiencyReportCard title="AI Proficiency over time" className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col"><div className="flex min-h-0 flex-1 flex-col"><ProficiencyOverTimeLegend showBenchmarks={hasBenchmarks} className="mb-4 shrink-0" /><div className="min-h-55 flex-1"><ProficiencyOverTimeChart labels={data.trendLabels} orgScores={data.orgScoresOverTime} benchmarks={data.trendBenchmarks} /></div></div></AiProficiencyReportCard></div></div><div className="grid grid-cols-4 items-start border-b border-b-lightborder"><div className="col-span-4"><AiProficiencyReportCard title="AI Proficiency groups" contentClassName="p-0"><ProficiencyLadder levels={data.levels} totalUsers={sumLevelUserCounts(data.levels)} levelBenchmarks={data.levelBenchmarks} comparison="teams" /></AiProficiencyReportCard></div></div><div className="grid grid-cols-4 items-start"><div className="col-span-4"><AiProficiencyReportCard title="AI Proficiency groups progress over time" contentClassName="p-0"><LevelMixChart labels={data.trendLabels} levelMix={data.levelMixOverTime} levelCounts={data.levelCountsOverTime} migration={data.migration} timeRangeLabel={data.migrationTimeRangeLabel} /></AiProficiencyReportCard></div></div></>;
    $[8] = data.isChartsLoading;
    $[9] = data.isInitialLoading;
    $[10] = data.levelBenchmarks;
    $[11] = data.levelCountsOverTime;
    $[12] = data.levelMixOverTime;
    $[13] = data.levels;
    $[14] = data.migration;
    $[15] = data.migrationTimeRangeLabel;
    $[16] = data.orgBenchmarks;
    $[17] = data.orgScore;
    $[18] = data.orgScoresOverTime;
    $[19] = data.trendBenchmarks;
    $[20] = data.trendLabels;
    $[21] = hasBenchmarks;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== methodologyOpen || $[24] !== projectId) {
    t5 = <MethodologyDrawer open={methodologyOpen} onOpenChange={setMethodologyOpen} projectId={projectId} queryKeySuffix="org" />;
    $[23] = methodologyOpen;
    $[24] = projectId;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== t3 || $[27] !== t4 || $[28] !== t5) {
    t6 = <div className="border-b border-b-lightborder" data-testid={t3}>{t4}{t5}</div>;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  return t6;
}